/**
 * SkateparkHours - Client-side day detection for opening hours display
 *
 * Automatically detects the current day of the week and updates block displays
 * with today's opening hours from data attributes.
 *
 * Usage:
 *   <div data-skatepark-hours
 *        data-hours-monday="12pm - 8pm"
 *        data-hours-tuesday="12pm - 8pm"
 *        ...>
 *     <span data-hours-display></span>
 *   </div>
 *
 * @class SkateparkHours
 */
// Prevent duplicate declaration if script is loaded multiple times
if (typeof SkateparkHours === 'undefined') {
class SkateparkHours {
  /**
   * Initialize hours display for a block element
   * @param {HTMLElement} element - Block element with data-skatepark-hours attribute
   */
  constructor(element) {
    if (!element) {
      console.error('SkateparkHours: element is required');
      return;
    }

    this.element = element;
    this.hoursDisplay = element.querySelector('[data-hours-display]');

    if (!this.hoursDisplay) {
      console.warn('SkateparkHours: [data-hours-display] element not found');
      return;
    }

    // Cache alert container reference
    this.alertContainer = element.querySelector('[data-alert-container]');

    // Initialize immediately
    this.updateHours();
    this.updateAlerts();
  }

  /**
   * Detect current day and update display with today's hours
   * Maps JavaScript Date.getDay() (0=Sunday, 6=Saturday) to data attributes
   */
  updateHours() {
    // Day name mapping (0=Sunday per JavaScript Date.getDay() spec)
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    // Get current day index (0-6)
    const today = new Date().getDay();
    const dayName = days[today] || 'monday'; // Fallback to monday if invalid

    // Convert to dataset property name (e.g., 'monday' -> 'hoursMonday')
    const datasetKey = `hours${dayName.charAt(0).toUpperCase()}${dayName.slice(1)}`;

    // Read hours from data attribute with fallback to default
    const hours = this.element.dataset[datasetKey] || '12pm - 8pm';

    // Update display (use textContent for XSS safety, not innerHTML)
    if (this.hoursDisplay) {
      this.hoursDisplay.textContent = hours;
    }
  }

  /**
   * Parse and display active alert based on current date
   * Applies priority: closure > event > modified_hours
   */
  updateAlerts() {
    if (!this.alertContainer) {
      return; // Block doesn't have alert support (backward compat)
    }

    const alertsData = this.element.dataset.alerts;
    const timezoneOffset = this.element.dataset.timezoneOffset;

    if (!alertsData || alertsData === '') {
      this.alertContainer.style.display = 'none';
      return;
    }

    const activeAlert = this.getActiveAlert(alertsData, timezoneOffset);

    if (activeAlert) {
      this.renderAlert(activeAlert);
    } else {
      this.alertContainer.style.display = 'none';
    }
  }

  /**
   * Parse alerts data and return highest-priority active alert
   * @param {string} alertsData - Pipe-separated alert strings
   * @param {string} timezoneOffset - Shopify timezone offset (e.g., '+0000')
   * @returns {Object|null} - Active alert object or null
   */
  getActiveAlert(alertsData, timezoneOffset) {
    const alertStrings = alertsData.split('|');
    const alerts = [];

    // Parse all alerts
    for (const alertString of alertStrings) {
      const [type, message, startDate, endDate] = alertString.split('::');

      // Only type and message are required (dates are optional)
      if (!type || !message) {
        console.warn('SkateparkHours: Invalid alert format', alertString);
        continue;
      }

      alerts.push({
        type,
        message,
        startDate: startDate || '',
        endDate: endDate || ''
      });
    }

    // Filter to active alerts based on date range
    const now = new Date();
    const activeAlerts = alerts.filter(alert => {
      const hasStartDate = alert.startDate && alert.startDate.trim() !== '';
      const hasEndDate = alert.endDate && alert.endDate.trim() !== '';

      // Case 1: No dates = always show
      if (!hasStartDate && !hasEndDate) {
        return true;
      }

      // Case 2: Start date only = show from start date onwards
      if (hasStartDate && !hasEndDate) {
        const start = this.parseDate(alert.startDate, timezoneOffset);
        if (!start) {
          return false; // Invalid date format
        }
        return now >= start;
      }

      // Case 3: Both dates = show in date range
      if (hasStartDate && hasEndDate) {
        const start = this.parseDate(alert.startDate, timezoneOffset);
        const end = this.parseDate(alert.endDate, timezoneOffset);

        if (!start || !end) {
          return false; // Invalid date format
        }

        // End date is INCLUSIVE (alert hides at midnight AFTER end date)
        const endOfDay = new Date(end);
        endOfDay.setHours(23, 59, 59, 999);

        return now >= start && now <= endOfDay;
      }

      // Case 4: End date only (no start) = treat as invalid, don't show
      return false;
    });

    if (activeAlerts.length === 0) {
      return null;
    }

    // Apply priority if multiple active alerts
    const priorityOrder = { closure: 1, event: 2, modified_hours: 3 };
    activeAlerts.sort((a, b) => {
      return priorityOrder[a.type] - priorityOrder[b.type];
    });

    return activeAlerts[0]; // Return highest priority
  }

  /**
   * Parse YYYY-MM-DD string to Date object with timezone awareness
   * @param {string} dateString - YYYY-MM-DD format
   * @param {string} timezoneOffset - Shopify timezone offset (e.g., '+0000')
   * @returns {Date|null} - Parsed date or null if invalid
   */
  parseDate(dateString, timezoneOffset) {
    // Validate format (basic regex)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      console.warn('SkateparkHours: Invalid date format', dateString);
      return null;
    }

    // Parse as local midnight (not UTC midnight)
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day, 0, 0, 0, 0);

    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  }

  /**
   * Render alert banner in DOM
   * @param {Object} alert - Alert object with type and message
   */
  renderAlert(alert) {
    // Map alert type to CSS modifier class
    const typeClass = `skatepark-info__alert--${alert.type}`;

    // Determine icon based on type
    const icons = {
      closure: '⚠️',
      event: '📅',
      modified_hours: '🕐'
    };
    const icon = icons[alert.type] || '📢';

    // Build HTML (using textContent for XSS safety)
    const iconSpan = document.createElement('span');
    iconSpan.className = 'skatepark-info__alert-icon';
    iconSpan.setAttribute('aria-hidden', 'true');
    iconSpan.textContent = icon;

    const messageSpan = document.createElement('span');
    messageSpan.className = 'skatepark-info__alert-message';
    messageSpan.textContent = alert.message; // XSS-safe

    // Clear previous content and append new
    this.alertContainer.innerHTML = '';
    this.alertContainer.className = `skatepark-info__alert ${typeClass}`;
    this.alertContainer.appendChild(iconSpan);
    this.alertContainer.appendChild(messageSpan);
    this.alertContainer.style.display = 'flex';
  }
}

/**
 * Auto-initialization on DOM ready
 * Initializes all elements with [data-skatepark-hours] attribute
 */
document.addEventListener('DOMContentLoaded', () => {
  const blocks = document.querySelectorAll('[data-skatepark-hours]');

  blocks.forEach(block => {
    new SkateparkHours(block);
  });
});
} // End of duplicate declaration guard
